import React, {useRef, useState} from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Features } from '../../assets/features/Features';
import { Credentials } from '../../assets/features/Credentials';
import { sendContactFormEmails } from '../../data/services/EmailService';
import SuccessDialog from '../../elements/dialog/SuccessDialog';

const FormOne = ({ isContactPage = false }) => {
    const { language } = useLanguage();
    const { theme } = useTheme();
    const form = useRef();
    const isDarkMode = theme === 'dark';

    const [ result, showresult ] = useState(false);
    const [ step, setStep ] = useState(1);
    const [ animating, setAnimating ] = useState(false);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recaptchaRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    
    // Handle form field changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNext = (e) => {
        e.preventDefault();
        // Validate step 1 fields
        if (step === 1) {
            const nameInput = form.current['contact-name'];
            const emailInput = form.current['contact-email'];
            const phoneInput = form.current['contact-phone'];
            
            let isValid = true;
            
            if (!nameInput.value.trim()) {
                nameInput.setCustomValidity(Features.FORM.VALIDATION_NAME[language]);
                isValid = false;
            } else {
                nameInput.setCustomValidity('');
            }
            
            if (!emailInput.value.trim()) {
                emailInput.setCustomValidity(Features.FORM.VALIDATION_EMAIL[language]);
                isValid = false;
            } else {
                emailInput.setCustomValidity('');
            }
            
            if (!phoneInput.value.trim()) {
                phoneInput.setCustomValidity(Features.FORM.VALIDATION_PHONE[language]);
                isValid = false;
            } else {
                phoneInput.setCustomValidity('');
            }
            
            if (isValid) {
                setAnimating(true);
                setTimeout(() => {
                    setStep(2);
                    setAnimating(false);
                }, 300);
            } else {
                form.current.reportValidity();
            }
        }
    };

    const handleBack = () => {
        setAnimating(true);
        setTimeout(() => {
            setStep(step - 1);
            setAnimating(false);
        }, 300);
    };

    const handleCaptchaChange = (token) => {
        setCaptchaVerified(!!token);
    };

    const sendEmail = async (e) => {
        e.preventDefault();
    
        // Validate reCAPTCHA
        if (!captchaVerified) {
            const recaptchaMessage = (Features.FORM.VALIDATION_RECAPTCHA && Features.FORM.VALIDATION_RECAPTCHA[language]) 
                ? Features.FORM.VALIDATION_RECAPTCHA[language] 
                : 'Please complete the reCAPTCHA verification';
            alert(recaptchaMessage);
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            return;
        }

        // Check if Email Cloud Function URL is configured
        if (!Credentials.EMAIL_CLOUD_FUNCTION_URL) {
            alert('Email service is not configured. Please contact the administrator.');
            return;
        }

        // Collect form data
        const getNameInput = () => {
            const input = form.current['contact-name'];
            return input ? input.value : '';
        };
        const getEmailInput = () => {
            const input = form.current['contact-email'];
            return input ? input.value : '';
        };
        const getPhoneInput = () => {
            const input = form.current['contact-phone'];
            return input ? input.value : '';
        };
        const getSubjectInput = () => {
            const input = form.current['contact-subject'];
            return input ? input.value : '';
        };
        const getMessageInput = () => {
            const input = form.current['contact-message'];
            return input ? input.value : '';
        };

        const formDataToSend = {
            name: formData.name || getNameInput() || '',
            email: formData.email || getEmailInput() || '',
            phone: formData.phone || getPhoneInput() || '',
            subject: formData.subject || getSubjectInput() || '',
            message: formData.message || getMessageInput() || ''
        };

        setIsSubmitting(true);

        // Optimistic UI: fire-and-forget the send, show success immediately
        sendContactFormEmails(formDataToSend)
            .then(() => {
                // Push GTM event for form success
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "form_success",
                    form_name: "orcamento_fielcarga",
                    page_path: window.location.pathname
                });
            })
            .catch(console.error);

        // Reset form immediately (optimistic)
        form.current.reset();
        setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
        });
        setCaptchaVerified(false);
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
        showresult(true);
        if (!isContactPage) {
            setStep(1);
        }
        setIsSubmitting(false);
      };

    const handleCloseDialog = () => {
            showresult(false);
    };


    // If contact page, show all fields without steps
    if (isContactPage) {
    return (
        <>
        <form ref={form} onSubmit={sendEmail} className="axil-contact-form">
        <div className="form-group">
                    <label>{Features.FORM.FULL_NAME[language]}</label>
                    <input 
                        type="text" 
                        className="form-control" 
                        name="contact-name" 
                        placeholder={Features.FORM.FULL_NAME[language]} 
                        required 
                        onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_NAME[language])}
                        onInput={(e) => e.target.setCustomValidity('')}
                    />
                </div>
                <div className="form-group">
                    <label>{Features.FORM.EMAIL[language]}</label>
                    <input 
                        type="email" 
                        className="form-control" 
                        name="contact-email" 
                        placeholder={Features.FORM.EMAIL[language]} 
                        required 
                        onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_EMAIL[language])}
                        onInput={(e) => e.target.setCustomValidity('')}
                    />
                </div>
                <div className="form-group">
                    <label>{Features.FORM.PHONE[language]}</label>
                    <input type="tel" className="form-control" name="contact-phone" placeholder={Features.FORM.PHONE[language]} />
                </div>
                <div className="form-group">
                    <label>{Features.FORM.SUBJECT[language]}</label>
                    <input type="text" className="form-control" name="contact-subject" placeholder={Features.FORM.SUBJECT[language]} />
                </div>
                <div className="form-group">
                    <label>{Features.FORM.MESSAGE[language]}</label>
                    <textarea className="form-control" name="contact-message" placeholder={Features.FORM.MESSAGE[language]} rows="4"></textarea>
                    <small 
                        className="form-helper-text"
                        style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'var(--color-body)' }}
                    >
                        {Features.FORM.MESSAGE_HELPER[language]}
                    </small>
                </div>
                {Credentials.RECAPTCHA_SITE_KEY && (
                    <div className="form-group" style={{marginBottom: '20px', display: 'flex', justifyContent: 'center'}}>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={Credentials.RECAPTCHA_SITE_KEY}
                            onChange={handleCaptchaChange}
                            theme={isDarkMode ? 'dark' : 'light'}
                        />
                    </div>
                )}
                <div className="form-group" style={{marginBottom: '20px'}}>
                    <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6'}}>
                        <input 
                            type="checkbox" 
                            name="contact-privacy-accept" 
                            required 
                            style={{marginRight: '10px', marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer'}}
                            onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_PRIVACY[language])}
                            onInput={(e) => e.target.setCustomValidity('')}
                        />
                        <span>
                            {Features.FORM.PRIVACY_CHECKBOX[language]} <Link to="/politica-de-privacidade" style={{color: 'var(--color-primary)', textDecoration: 'underline'}}>{Features.FORM.PRIVACY_POLICY[language]}</Link> {Features.FORM.AND[language]} <Link to="/termos-de-uso" style={{color: 'var(--color-primary)', textDecoration: 'underline'}}>{Features.FORM.TERMS_CONDITIONS[language]}</Link>.
                        </span>
                    </label>
                </div>
                <div className="form-group mb--40">
                    <button 
                        type="submit" 
                        className="axil-btn btn-fill-primary btn-fluid btn-primary"
                        disabled={isSubmitting}
                        style={{ 
                            opacity: isSubmitting ? 0.7 : 1, 
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}
                    >
                        {isSubmitting && <span className="btn-spinner"></span>}
                        {isSubmitting ? Features.FORM.SENDING[language] : Features.FORM.SEND[language]}
                    </button>
                </div>
                <style jsx>{`
                    .form-group {
                        margin-bottom: 18px;
                    }
                    .form-group label {
                        font-size: 14px;
                        margin-bottom: 6px;
                        display: block;
                    }
                    .form-group input,
                    .form-group textarea,
                    .form-group select {
                        font-size: 14px;
                        padding: 10px 15px;
                        width: 100%;
                    }
                    .form-helper-text {
                        display: block;
                        margin-top: 6px;
                        font-size: 12px;
                        color: var(--color-body);
                        opacity: 0.7;
                    }
                    .btn-spinner {
                        width: 18px;
                        height: 18px;
                        border: 2px solid rgba(255, 255, 255, 0.3);
                        border-top-color: #ffffff;
                        border-radius: 50%;
                        animation: spin 0.8s linear infinite;
                    }
                    @keyframes spin {
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>
            </form>
            <SuccessDialog 
                isOpen={result} 
                onClose={handleCloseDialog} 
                message={Features.FORM.SUCCESS_MESSAGE[language]} 
            />
        </>
        );
    }

    // Home page with steps
    return (
        <>
        <form ref={form} onSubmit={step < 2 ? handleNext : sendEmail} className="axil-contact-form">
        
        {/* Progress Bar */}
        <div className="form-progress-wrapper mb-4">
            <div className="form-progress-bar">
                <div 
                    className="form-progress-fill" 
                    style={{
                        width: `${(step / 2) * 100}%`,
                        background: isDarkMode 
                            ? 'linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%)' 
                            : 'linear-gradient(90deg, #2f96d4 0%, #1e7ba8 100%)'
                    }}
                ></div>
            </div>
            <div 
                className="form-progress-text"
                style={{ color: isDarkMode ? '#ffffff' : 'var(--color-body)' }}
            >
                {Features.FORM.STEP[language]} {step} {Features.FORM.OF[language]} 2
            </div>
        </div>

        <div className={`form-step-container ${animating ? 'fade-out' : 'fade-in'}`}>
        {step === 1 && (
            <>
                <div className="form-group">
                    <label>{Features.FORM.FULL_NAME[language]}</label>
                    <input 
                        type="text" 
                        className="form-control" 
                        name="contact-name" 
                        placeholder={Features.FORM.FULL_NAME[language]} 
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required 
                        onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_NAME[language])}
                        onInput={(e) => e.target.setCustomValidity('')}
                    />
        </div>
        <div className="form-group">
                    <label>{Features.FORM.EMAIL[language]}</label>
                    <input 
                        type="email" 
                        className="form-control" 
                        name="contact-email" 
                        placeholder={Features.FORM.EMAIL[language]} 
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required 
                        onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_EMAIL[language])}
                        onInput={(e) => e.target.setCustomValidity('')}
                    />
        </div>
        <div className="form-group mb--40">
                    <label>{Features.FORM.PHONE[language]}</label>
                    <input 
                        type="tel" 
                        className="form-control" 
                        name="contact-phone" 
                        placeholder={Features.FORM.PHONE[language]} 
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required 
                        onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_PHONE[language])}
                        onInput={(e) => e.target.setCustomValidity('')}
                    />
        </div>
        <div className="form-group">
                    <button type="submit" className="axil-btn btn-fill-primary btn-fluid btn-primary">{Features.FORM.NEXT[language]}</button>
                </div>
            </>
        )}

        {step === 2 && (
            <>
                <div className="form-group">
                    <label>{Features.FORM.SUBJECT[language]}</label>
                    <input 
                        type="text" 
                        className="form-control" 
                        name="contact-subject" 
                        placeholder={Features.FORM.SUBJECT[language]}
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                    />
                </div>
                <div className="form-group mb--40">
                    <label>{Features.FORM.MESSAGE[language]}</label>
                    <textarea 
                        className="form-control" 
                        name="contact-message" 
                        placeholder={Features.FORM.MESSAGE[language]} 
                        rows="4"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                    ></textarea>
                    <small 
                        className="form-helper-text"
                        style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'var(--color-body)' }}
                    >
                        {Features.FORM.MESSAGE_HELPER[language]}
                    </small>
                </div>
                {Credentials.RECAPTCHA_SITE_KEY && (
                    <div className="form-group" style={{marginBottom: '20px', display: 'flex', justifyContent: 'center'}}>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={Credentials.RECAPTCHA_SITE_KEY}
                            onChange={handleCaptchaChange}
                            theme={isDarkMode ? 'dark' : 'light'}
                        />
                    </div>
                )}
                <div className="form-group" style={{marginBottom: '20px'}}>
                    <label style={{display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6'}}>
                        <input 
                            type="checkbox" 
                            name="contact-privacy-accept" 
                            required 
                            style={{marginRight: '10px', marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer'}}
                            onInvalid={(e) => e.target.setCustomValidity(Features.FORM.VALIDATION_PRIVACY[language])}
                            onInput={(e) => e.target.setCustomValidity('')}
                        />
                        <span>
                            {Features.FORM.PRIVACY_CHECKBOX[language]} <Link to="/politica-de-privacidade" style={{color: 'var(--color-primary)', textDecoration: 'underline'}}>{Features.FORM.PRIVACY_POLICY[language]}</Link> {Features.FORM.AND[language]} <Link to="/termos-de-uso" style={{color: 'var(--color-primary)', textDecoration: 'underline'}}>{Features.FORM.TERMS_CONDITIONS[language]}</Link>.
                        </span>
                    </label>
                </div>
                <div className="form-group d-flex gap-3">
                    <button 
                        type="button" 
                        onClick={handleBack} 
                        className="axil-btn btn-fill-white btn-fluid"
                        disabled={isSubmitting}
                        style={{ opacity: isSubmitting ? 0.5 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    >
                        {Features.FORM.BACK[language]}
                    </button>
                    <button 
                        type="submit" 
                        className="axil-btn btn-fill-primary btn-fluid btn-primary"
                        disabled={isSubmitting}
                        style={{ 
                            opacity: isSubmitting ? 0.7 : 1, 
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}
                    >
                        {isSubmitting && <span className="btn-spinner"></span>}
                        {isSubmitting ? Features.FORM.SENDING[language] : Features.FORM.SEND[language]}
                    </button>
                </div>
            </>
        )}
        </div>

        <style jsx>{`
            .form-progress-wrapper {
                margin-bottom: 20px;
            }
            
            .form-progress-bar {
                width: 100%;
                height: 6px;
                background-color: rgba(0, 0, 0, 0.1);
                border-radius: 10px;
                overflow: hidden;
                margin-bottom: 8px;
            }
            
            :global(body.active-dark-mode .form-progress-bar) {
                background-color: rgba(255, 255, 255, 0.2) !important;
            }
            
            .form-progress-fill {
                height: 100%;
                transition: width 0.4s ease-in-out, background 0.3s ease-in-out;
                border-radius: 10px;
            }
            
            .form-progress-text {
                text-align: center;
                font-size: 13px;
                font-weight: 500;
                transition: color 0.3s ease-in-out;
            }
            
            .form-step-container {
                transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
            }
            
            .form-step-container.fade-in {
                opacity: 1;
                transform: translateX(0);
                animation: slideIn 0.3s ease-in-out;
            }
            
            .form-step-container.fade-out {
                opacity: 0;
                transform: translateX(-20px);
            }
            
            .form-row {
                margin-left: -10px;
                margin-right: -10px;
            }
            
            .form-row .form-group {
                padding-left: 10px;
                padding-right: 10px;
            }
            
            .form-group {
                margin-bottom: 18px;
            }
            
            .form-group label {
                font-size: 14px;
                margin-bottom: 6px;
            }
            
            .form-group input,
            .form-group textarea,
            .form-group select {
                font-size: 14px;
                padding: 10px 15px;
            }
            .form-helper-text {
                display: block;
                margin-top: 6px;
                font-size: 12px;
                color: var(--color-body);
                opacity: 0.7;
            }
            
            @keyframes slideIn {
                from {
                    opacity: 0;
                    transform: translateX(20px);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }
            
            .btn-spinner {
                width: 18px;
                height: 18px;
                border: 2px solid rgba(255, 255, 255, 0.3);
                border-top-color: #ffffff;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
            }
            
            @keyframes spin {
                to {
                    transform: rotate(360deg);
                }
            }
        `}</style>

    </form>
    <SuccessDialog 
        isOpen={result} 
        onClose={handleCloseDialog} 
        message={Features.FORM.SUCCESS_MESSAGE[language]} 
    />
    </>
    )
}

export default FormOne;